import cx from "classnames";
import { Icon } from "common/Icon";
import { COPY_TO_ROUTES_MODAL } from "common/modals/CopyToRoutesModal";
import { DELETE_RECORD_MODAL } from "common/modals/DeleteRecordModal";
import { EDIT_TROUTE_MODAL } from "common/modals/EditTRouteModal";
import { EXPORT_TROUTE_MODAL } from "common/modals/ExportTRouteModal";
import { PRINT_LOGGED_OUT_MODAL } from "common/modals/PrintLoggedOutModal";
import { PRINT_OFFICIAL_PDF_MODAL } from "common/modals/PrintOfficialPDFModal";
import { PRINT_UPSELL_MODAL } from "common/modals/PrintUpsellModal";
import { REPLACE_ELEVATION_MODAL } from "common/modals/ReplaceElevationModal";
import { SHARE_MODAL } from "common/modals/ShareModal";
import {
  PopoverLinkMenu,
  PopoverLinkMenuHeading as MenuHeading,
  PopoverLinkMenuItem as MenuItem,
} from "common/styleguide/PopoverLinkMenu";
import { favoriteActions, favoriteSelectors } from "data/resources/favorites";
import { commonActions, commonNouns, commonVerbs, featureNames, Msg } from "lib/localization";
import { showModal } from "lib/modalUtils";
import { urls } from "lib/urls";
import { currentURLWithQueryParams, urlWithPrivacyCode } from "lib/utils";
import _ from "lodash";
import React, { Fragment } from "react";
import { defineMessages, FormattedMessage as T, injectIntl } from "react-intl";
import { connect } from "react-redux";
import styles from "./TRouteSidePanelActions.scss";

export const spMessages = defineMessages({
  editingDisabled: {
    id: "spMessages.editingDisabled",
    defaultMessage: "Editing is disabled because this route has been locked",
  },
  printMap: {
    id: "spMessages.printMap",
    defaultMessage: "Print Map PDF",
  },
  printMapAndCue: {
    id: "spMessages.printMapAndCue",
    defaultMessage: "Print Map & Cue PDF",
  },
});

const routePlannerMapOptionParams = (mapRef) => {
  if (mapRef) {
    const viewport = mapRef._lastViewport;
    const overlay = mapRef._mapEngine.props.overlay.id;
    return `center=${viewport.center.lat},${viewport.center.lng}&zoom=${viewport.zoom}&overlay=${overlay}`;
  }
};

class EditButton extends React.PureComponent {
  editWithViewport = (e) => {
    e.preventDefault();
    window.location = `${e.currentTarget.href}?${routePlannerMapOptionParams(this.props.mapRef)}`;
  };

  render() {
    const { intl, mini, troute, objectType } = this.props;
    const editURL = urls[`${objectType}s`].edit(troute.id);

    // we display the edit button whenever the route can normally be edited by
    // the user, but we grey it out and add a tooltip when it's locked by the
    // parent org account.
    const displayEditButton = troute.permissions && troute.permissions.canBeModified;
    if (!displayEditButton) {
      return null;
    }
    const disableEditButton = troute.locked;
    const EditIcon = (
      <Fragment>
        <Icon i="md-create" /> {!mini && intl.formatMessage(commonVerbs.edit)}
      </Fragment>
    );

    if (disableEditButton) {
      return (
        <div style={{ opacity: 0.4 }} data-tip title={intl.formatMessage(spMessages.editingDisabled)}>
          {EditIcon}
        </div>
      );
    }

    if (objectType === "route") {
      return (
        <a href={editURL} onClick={this.editWithViewport}>
          {EditIcon}
        </a>
      );
    }

    return <a onClick={() => showModal(EDIT_TROUTE_MODAL, { troute, objectType })}>{EditIcon}</a>;
  }
}

class _TRouteSidePanelActions extends React.PureComponent {
  state = {
    showMore: false,
  };

  toggleMore = () => {
    this.setState({ showMore: !this.state.showMore });
  };

  render() {
    const { troute, objectType, favorite, toggleFavorite, currentUser, mini, mapRef, intl } = this.props;
    const { showMore } = this.state;
    const shareModalProps = {
      objectType,
      objectId: troute.id,
      objectTitle: troute.name,
      objectUrl: urls[`${objectType}s`].show(troute.id, troute.privacyCode),
      privacyCode: troute.privacyCode,
    };

    return (
      <div className={cx(styles.TRouteSidePanelActions, mini && styles.mini)}>
        <div onClick={toggleFavorite}>
          <Icon
            i="pin"
            data-tip
            title={mini && _.capitalize(intl.formatMessage(favorite ? commonVerbs.pinned : commonVerbs.pin))}
          />
          {!mini && ` ${_.capitalize(intl.formatMessage(favorite ? commonVerbs.pinned : commonVerbs.pin))}`}
        </div>
        <div onClick={() => showModal(SHARE_MODAL, shareModalProps)}>
          <Icon i="md-share" /> {!mini && intl.formatMessage(commonVerbs.share)}
        </div>
        {<EditButton intl={intl} mini={mini} troute={troute} objectType={objectType} mapRef={mapRef} />}
        {!mini && (
          <div
            ref={(r) => {
              this._more = r;
            }}
            className={styles.more}
            onClick={this.toggleMore}
          >
            {intl.formatMessage(featureNames.more)} <Icon i="ios-arrow-down" />
          </div>
        )}
        {showMore && (
          <MoreMenu
            target={this._more}
            intl={intl}
            mapRef={mapRef}
            troute={troute}
            objectType={objectType}
            currentUser={currentUser}
            onRequestClose={this.toggleMore}
          />
        )}
      </div>
    );
  }
}

class MoreMenu extends React.PureComponent {
  showModal = (modal, props) => {
    showModal(modal, props);
    this.props.onRequestClose();
  };

  print = () => {
    const { troute, objectType, currentUser } = this.props;
    const { navigate } = troute.permissions;

    switch (true) {
      case !!troute.officialMapPdfUrl:
        // if it's a club route with an official PDF, just show that
        this.showModal(PRINT_OFFICIAL_PDF_MODAL, { troute, objectType });
        return;
      case !currentUser.id:
        // require login to print
        this.showModal(PRINT_LOGGED_OUT_MODAL);
        return;
      case window.rwgps.config.privileges.printing || navigate:
        // if the user can fancy-print, do that
        window.open(`/${objectType}s/${troute.id}/print`);
        return;
      default:
        // otherwise, upsell (which contains a link to starter print page)
        this.showModal(PRINT_UPSELL_MODAL, { troute, objectType });
    }
  };

  render() {
    const { objectType, target, troute, currentUser, mapRef, intl } = this.props;
    // classic mode is the current URL, with a ?beta=false querystring param added
    const classicMode = currentURLWithQueryParams({ beta: false });

    const editable = troute.permissions.canBeModified;
    const locked = troute.locked;

    return (
      <PopoverLinkMenu
        intl={intl}
        container={window.body}
        target={target}
        placement="bottom"
        onRequestClose={this.props.onRequestClose}
      >
        <MenuItem href={classicMode}>
          <T id="MoreMenu.classicMode" defaultMessage="View in Classic Mode" />
        </MenuItem>
        <MenuItem onClick={() => this.showModal(EXPORT_TROUTE_MODAL, { troute, objectType })}>
          <T id="MoreMenu.exportAsFile" defaultMessage="Export as File" />
        </MenuItem>
        <MenuItem onClick={this.print}>
          <Msg m={objectType === "route" ? spMessages.printMapAndCue : spMessages.printMap} />
        </MenuItem>
        {objectType === "route" && (
          <MenuItem href={`/routes/${troute.id}/cue_sheet`} target="_blank">
            <T id="MoreMenu.printCuesheet" defaultMessage="Print Cuesheet" />
          </MenuItem>
        )}
        {currentUser.id && troute.permissions.canBeCopied && (
          <>
            <MenuItem
              href={urlWithPrivacyCode(
                `/routes/new?importType=${objectType}&importId=${troute.id}&${routePlannerMapOptionParams(mapRef)}`,
                troute.privacyCode
              )}
            >
              <T id="MoreMenu.openInPlanner" defaultMessage="Open in Route Planner" />
            </MenuItem>
            <MenuItem onClick={() => this.showModal(COPY_TO_ROUTES_MODAL, { troute, objectType })}>
              <Msg m={commonActions.copyToMyRoutes} />
            </MenuItem>
          </>
        )}
        {editable && (
          <Fragment>
            <MenuHeading>
              <T id="MoreMenu.privateActions" defaultMessage="Private Actions" />
            </MenuHeading>
            {!locked && (
              <MenuItem
                onClick={() =>
                  this.showModal(REPLACE_ELEVATION_MODAL, {
                    troute,
                    objectType,
                  })
                }
              >
                <T id="MoreMenu.replaceElevation" defaultMessage="Replace Elevation Data" />
              </MenuItem>
            )}
            <MenuItem
              onClick={() =>
                this.showModal(DELETE_RECORD_MODAL, {
                  object: troute,
                  objectType,
                  objectMessage: commonNouns.routes,
                  objectName: troute.name,
                })
              }
            >
              <Msg m={commonVerbs.delete} />
            </MenuItem>
          </Fragment>
        )}
      </PopoverLinkMenu>
    );
  }
}

const mapStateToProps = (state, ownProps) => ({
  favorite: favoriteSelectors.favoriteForObject(state, ownProps.objectType, ownProps.troute.id),
  currentUser: state.currentUser,
});

const mapDispatchToProps = (dispatch, ownProps) => ({
  toggleFavorite: () => dispatch(favoriteActions.toggle(ownProps.objectType, ownProps.troute.id, "web")),
});

export const TRouteSidePanelActions = connect(mapStateToProps, mapDispatchToProps)(injectIntl(_TRouteSidePanelActions));
